import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Pill } from "@/components/ui/Primitives";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Trash2, AlertTriangle, Linkedin, CreditCard, Sparkles, Activity, Mail, Megaphone } from "lucide-react";
import { confirmDialog } from "@/components/ConfirmDialog";
import { DeleteAccountDialog } from "@/components/settings/DeleteAccountDialog";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [socialStatus, setSocialStatus] = useState(null);
  const [marketingBusy, setMarketingBusy] = useState(false);
  const [marketingStats, setMarketingStats] = useState(null);
  const [adsBusy, setAdsBusy] = useState(false);
  const [adCreative, setAdCreative] = useState(null);
  const [composeText, setComposeText] = useState("");
  const [composeTarget, setComposeTarget] = useState("both");
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeResult, setComposeResult] = useState(null);
  const [metaInput, setMetaInput] = useState("");
  const [metaKind, setMetaKind] = useState("blog");
  const [metaTone, setMetaTone] = useState("professional");
  const [metaBusy, setMetaBusy] = useState(false);
  const [metaResult, setMetaResult] = useState(null);
  const [analyticsBusy, setAnalyticsBusy] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [analyticsResult, setAnalyticsResult] = useState(null);
  const [autopostState, setAutopostState] = useState(null);
  const [autopostBusy, setAutopostBusy] = useState(false);
  const isLandlord = user?.role === "landlord";
  // Only the account owner sees the maintenance tools (matches the
  // contact email used everywhere else as a soft owner gate — actual
  // mutating endpoint still requires ADMIN_CONFIG_TOKEN).
  const isOwner = isLandlord && (user?.email || "").toLowerCase() === "contact@centralrentals.ca";

  const handleSignOut = async () => {
    setSigningOut(true);
    const portal = user?.role === "tenant" ? "tenant" : "landlord";
    try { localStorage.setItem("crc_last_portal", portal); } catch { /* noop */ }
    try { await logout(); } catch { /* noop */ }
    nav(`/login/${portal}`, { replace: true });
  };

  const connectLinkedIn = () => {
    // Member-mode OAuth bridge — works with the basic w_member_social
    // scope, no LinkedIn Community Management API approval required.
    if (!API_BASE) { toast.error("Backend URL not configured."); return; }
    window.open(`${API_BASE}/api/linkedin/login?mode=member`, "_blank", "noopener,noreferrer");
    toast.success("Authorize Central Rentals in the new tab, then return here.");
  };

  const openStripeConnect = () => {
    window.open("https://dashboard.stripe.com/settings/connect/platform-profile", "_blank", "noopener,noreferrer");
    toast.info("Complete your Connect platform profile, then redeploy with live Stripe keys.");
  };

  const runBlogDedupe = async () => {
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN to run blog dedupe on this environment:");
    if (!token) return;
    setDedupeBusy(true);
    try {
      const { data } = await api.post("/admin/blog/dedupe", { token });
      const removed = (data?.deleted || []).length;
      const fixes = data?.image_fixes_applied || 0;
      toast.success(`Dedupe complete — removed ${removed} duplicate post${removed === 1 ? "" : "s"}, fixed ${fixes} image${fixes === 1 ? "" : "s"}.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Dedupe failed.";
      toast.error(typeof msg === "string" ? msg : "Dedupe failed.");
    } finally {
      setDedupeBusy(false);
    }
  };

  const refreshSocialStatus = async () => {
    setStatusBusy(true);
    try {
      const { data } = await api.get("/social/status");
      setSocialStatus(data);
      toast.success("Social status refreshed.");
    } catch {
      toast.error("Could not fetch social status.");
    } finally {
      setStatusBusy(false);
    }
  };

  const runMarketingNow = async () => {
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN to send the next marketing batch:");
    if (!token) return;
    setMarketingBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/send-now", { token, limit: 100 });
      toast.success(`Sent ${data?.sent || 0} marketing email${data?.sent === 1 ? "" : "s"}.`);
      // Refresh stats too
      try {
        const r = await api.get(`/admin/marketing/stats?token=${encodeURIComponent(token)}`);
        setMarketingStats(r.data);
      } catch { /* noop */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Marketing send failed.");
    } finally {
      setMarketingBusy(false);
    }
  };

  const refreshMarketingStats = async () => {
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN:");
    if (!token) return;
    try {
      const { data } = await api.get(`/admin/marketing/stats?token=${encodeURIComponent(token)}`);
      setMarketingStats(data);
      toast.success(`${data?.active || 0} active subscribers.`);
    } catch {
      toast.error("Could not fetch marketing stats.");
    }
  };

  const generateAds = async () => {
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN to generate ad copy for the latest blog post:");
    if (!token) return;
    setAdsBusy(true);
    try {
      const { data } = await api.post("/admin/ads/generate", { token });
      setAdCreative(data);
      toast.success(`Generated ${(data?.google || []).length} Google + ${(data?.meta || []).length} Meta ad variants.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ad generation failed.");
    } finally {
      setAdsBusy(false);
    }
  };

  const loadAutopost = async (tok) => {
    const t = tok || window.prompt("Enter ADMIN_CONFIG_TOKEN:");
    if (!t) return null;
    try {
      const { data } = await api.get(`/admin/linkedin-autopost/state?token=${encodeURIComponent(t)}`);
      setAutopostState({ ...data, _token: t });
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-post state fetch failed");
      return null;
    }
  };

  const toggleAutopost = async (enabled) => {
    const t = autopostState?._token || window.prompt("Enter ADMIN_CONFIG_TOKEN:");
    if (!t) return;
    setAutopostBusy(true);
    try {
      await api.post("/admin/linkedin-autopost/toggle", { token: t, enabled, interval_hours: autopostState?.interval_hours || 5 });
      toast.success(`Auto-post ${enabled ? "enabled" : "paused"}.`);
      await loadAutopost(t);
    } catch (err) { toast.error(err?.response?.data?.detail || "Toggle failed"); }
    finally { setAutopostBusy(false); }
  };

  const runAutopostNow = async () => {
    const t = autopostState?._token || window.prompt("Enter ADMIN_CONFIG_TOKEN:");
    if (!t) return;
    setAutopostBusy(true);
    try {
      const { data } = await api.post("/admin/linkedin-autopost/run-now", { token: t });
      if (data?.ok) toast.success(`Posted to ${Object.keys(data.results || {}).join(", ")}`);
      else toast.warning(data?.reason || "Run completed with no destinations.");
      await loadAutopost(t);
    } catch (err) { toast.error(err?.response?.data?.detail || "Run-now failed"); }
    finally { setAutopostBusy(false); }
  };

  const generateMeta = async () => {
    const input = metaInput.trim();
    if (input.length < 3) { toast.error("Describe the page or topic first (≥ 3 chars)."); return; }
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN to generate meta tags:");
    if (!token) return;
    setMetaBusy(true);
    setMetaResult(null);
    try {
      const { data } = await api.post("/admin/meta/generate", { token, input, kind: metaKind, tone: metaTone });
      setMetaResult(data);
      toast.success("Meta tags generated.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Meta generation failed.";
      toast.error(typeof msg === "string" ? msg : "Meta generation failed.");
    } finally { setMetaBusy(false); }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const loadAnalytics = async () => {
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN to load analytics:");
    if (!token) return;
    setAnalyticsBusy(true);
    try {
      const { data } = await api.get(`/admin/analytics/summary?token=${encodeURIComponent(token)}&days=${analyticsDays}`);
      setAnalyticsResult(data);
      toast.success(`Loaded ${data?.total_events || 0} events over ${data?.window_days}d.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Analytics fetch failed.";
      toast.error(typeof msg === "string" ? msg : "Analytics fetch failed.");
    } finally { setAnalyticsBusy(false); }
  };

  const composeLinkedInPost = async () => {
    const text = composeText.trim();
    if (!text) { toast.error("Write something first."); return; }
    if (text.length > 3000) { toast.error("LinkedIn caps posts at 3000 characters."); return; }
    const token = window.prompt("Enter ADMIN_CONFIG_TOKEN to send this LinkedIn post:");
    if (!token) return;
    setComposeBusy(true);
    setComposeResult(null);
    try {
      const { data } = await api.post("/admin/linkedin/post", { token, text, target: composeTarget });
      setComposeResult(data);
      const ok = Object.entries(data?.targets || {}).filter(([, v]) => v.ok);
      const fail = Object.entries(data?.targets || {}).filter(([, v]) => !v.ok);
      if (ok.length && !fail.length) toast.success(`Posted to ${ok.map(([k]) => k).join(", ")}.`);
      else if (ok.length && fail.length) toast.warning(`Posted to ${ok.map(([k]) => k).join(", ")}; failed: ${fail.map(([k]) => k).join(", ")}.`);
      else toast.error("LinkedIn rejected the post — see details below.");
      if (ok.length) setComposeText("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Manual post failed.";
      toast.error(typeof msg === "string" ? msg : "Manual post failed.");
    } finally {
      setComposeBusy(false);
    }
  };

  const resetData = async () => {
    const ok = await confirmDialog({
      title: "Reset all your data?",
      description: "This deletes ALL your properties, tours, tickets, applications, expenses, and documents.\n\nYour account and subscription stay.\n\nThis cannot be undone.",
      confirmLabel: "Yes, reset everything",
      cancelLabel: "Keep my data",
      destructive: true,
    });
    if (!ok) return;
    setResetting(true);
    try {
      const { data } = await api.post("/users/me/reset-data");
      toast.success(`Cleared ${data.deleted || 0} record${data.deleted === 1 ? "" : "s"}. You're starting fresh.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset your data. Try again or email support.");
    } finally { setResetting(false); }
  };

  const accountDeleted = async (message) => {
    toast.success(message || "Your account has been deleted.");
    await logout();
    nav("/", { replace: true });
  };

  return (
    <div className="min-w-0 w-full settings-page" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage your account and preferences."/>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 min-w-0 [&>*]:min-w-0 [&_button]:max-w-full [&_button]:whitespace-normal">
        <Card className="p-4 sm:p-6" data-testid="settings-account-card">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Account</h2>
          <div className="space-y-3 text-sm">
            <Row label="Name" value={user?.name}/>
            <Row label="Email" value={user?.email}/>
            <Row label="User ID" value={<span className="font-mono text-xs">{user?.user_id || user?.id}</span>}/>
            <Row label="Auth method" value={<Pill tone="slate">{user?.auth_method || "—"}</Pill>}/>
            <Row label="Role" value={<Pill tone="green" className="capitalize">{user?.role || "none"}</Pill>}/>
            {isLandlord && (
              <Row label="Subscription" value={<Pill tone={user?.subscription_status === "active" ? "green" : "amber"}>{user?.subscription_status || "inactive"}</Pill>}/>
            )}
          </div>
          <Button variant="secondary" onClick={handleSignOut} disabled={signingOut} className="w-full mt-5" data-testid="settings-signout-button">
            <LogOut className="w-4 h-4"/> {signingOut ? "Signing out…" : "Sign out"}
          </Button>
        </Card>

        <Card className="p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-none"><Lock className="w-5 h-5"/></div>
            <div className="min-w-0">
              <h2 className="font-display text-lg font-bold text-slate-900">Your role is locked</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Your account is permanently set up as a <b className="capitalize">{user?.role || "—"}</b>. Landlord and tenant accounts are kept separate for security.
                If you need a different role, please <a href="mailto:contact@centralrentals.ca" data-testid="settings-role-support" className="text-green-800 font-semibold hover:underline">contact support</a> or create a new account with a different email.
              </p>
            </div>
          </div>
        </Card>

        <EmailPreferencesCard/>

        <Card className="p-6 md:col-span-2">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Need help?</h2>
          <p className="text-sm text-slate-500 mb-4">Our team is here Mon–Fri, 9am–6pm MT.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <a href="mailto:contact@centralrentals.ca" data-testid="settings-email" className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-green-800 hover:bg-green-50 transition-colors">
              <div className="w-11 h-11 shrink-0 rounded-lg bg-green-50 text-green-800 flex items-center justify-center">✉</div>
              <div className="min-w-0">
                <div className="text-xs text-slate-500">Email us</div>
                <div className="font-semibold text-slate-900 break-all">contact@centralrentals.ca</div>
              </div>
            </a>
          </div>
        </Card>

        <Card className="p-6 md:col-span-2 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-none"><AlertTriangle className="w-5 h-5"/></div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-lg font-bold text-slate-900">Reset all my data</h2>
              <p className="text-sm text-slate-600 mt-1 mb-4 leading-relaxed">
                Permanently delete every property, unit, tour, ticket, application, vendor, expense and document on your account so you can start fresh. Your account, subscription and contact info stay.
              </p>
              <Button variant="secondary" onClick={resetData} disabled={resetting} data-testid="reset-my-data-button">
                <Trash2 className="w-4 h-4"/> {resetting ? "Resetting…" : "Reset all my data"}
              </Button>
            </div>
          </div>
        </Card>

        {isOwner && (
          <Card className="p-6 md:col-span-2 border-sky-200" data-testid="owner-admin-tools">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-none"><Sparkles className="w-5 h-5"/></div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h2 className="font-display text-lg font-bold text-slate-900">Owner admin tools</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href="/admin/marketing" data-testid="owner-open-marketing"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold">
                      <Sparkles className="w-3.5 h-3.5"/> Marketing Dashboard →
                    </a>
                    <a href="/admin/growth-os" data-testid="owner-open-growth-os"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                      <Sparkles className="w-3.5 h-3.5"/> Growth OS →
                    </a>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">One-click maintenance actions. The destructive ones still require <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">ADMIN_CONFIG_TOKEN</code> to confirm.</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <button type="button" onClick={connectLinkedIn} data-testid="admin-connect-linkedin"
                className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-sky-500 hover:bg-sky-50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-none"><Linkedin className="w-5 h-5"/></div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">Connect LinkedIn (member mode)</div>
                  <div className="text-xs text-slate-500 mt-0.5">Authorize personal-profile auto-posting. No Community API approval needed.</div>
                </div>
              </button>

              <button type="button" onClick={openStripeConnect} data-testid="admin-stripe-connect"
                className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-violet-500 hover:bg-violet-50 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center flex-none"><CreditCard className="w-5 h-5"/></div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">Stripe Connect — platform profile</div>
                  <div className="text-xs text-slate-500 mt-0.5">Open the Stripe dashboard page that needs completion before tenant rent payments go live.</div>
                </div>
              </button>

              <button type="button" onClick={runBlogDedupe} disabled={dedupeBusy} data-testid="admin-blog-dedupe"
                className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none"><Sparkles className="w-5 h-5"/></div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{dedupeBusy ? "Cleaning…" : "Dedupe blog posts"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Keeps the oldest post per duplicate title, re-applies canonical Canadian-houses hero image.</div>
                </div>
              </button>

              <button type="button" onClick={refreshSocialStatus} disabled={statusBusy} data-testid="admin-social-status"
                className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-colors disabled:opacity-60">
                <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-none"><Activity className="w-5 h-5"/></div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{statusBusy ? "Checking…" : "Check social posting status"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Verifies which providers are configured and shows the most recent auto-post.</div>
                </div>
              </button>
            </div>

            {socialStatus && (
              <div className="mt-5 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="admin-social-status-result">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Pill tone={socialStatus.facebook_configured ? "green" : "amber"}>
                    Facebook: {socialStatus.facebook_configured ? "connected" : "not connected"}
                  </Pill>
                  <Pill tone={socialStatus.linkedin_configured ? "green" : "amber"}>
                    LinkedIn: {socialStatus.linkedin_configured ? "connected" : "not connected"}
                  </Pill>
                  {socialStatus.linkedin_org_configured && <Pill tone="slate">LinkedIn org URN: set</Pill>}
                </div>
                {Array.isArray(socialStatus.recent) && socialStatus.recent.length > 0 ? (
                  <div className="space-y-1.5">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recent posts</div>
                    {socialStatus.recent.slice(0, 5).map((r, i) => (
                      <div key={i} className="text-xs flex items-center justify-between gap-2 py-1 border-b border-slate-200 last:border-0">
                        <span className="font-mono text-slate-700 truncate">{r.provider}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{r.ok ? "ok" : "fail"}</span>
                        <span className="text-slate-500 truncate flex-1 text-right">{r.at || r.error || ""}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">No recent posts logged yet.</div>
                )}
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-violet-700"/> Smart meta-tag composer
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Paste a topic, URL, or paragraph. Claude returns SEO-ready title/description, OG &amp; Twitter card copy, FB/LinkedIn captions, hashtags, and a schema.org JSON-LD blob you can paste straight into &lt;head&gt;.
              </p>
              <textarea
                value={metaInput}
                onChange={(e) => setMetaInput(e.target.value)}
                placeholder='e.g. "How Alberta landlords prepare for a 2026 rent-increase notice" — or paste a full URL.'
                rows={3}
                data-testid="meta-input"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 resize-y"
                maxLength={2000}
              />
              <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {["blog", "webpage", "product", "social"].map((k) => (
                    <button key={k} type="button" onClick={() => setMetaKind(k)} data-testid={`meta-kind-${k}`}
                      className={`text-xs px-2.5 py-1 rounded-full border ${metaKind === k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:border-violet-400"}`}>
                      {k}
                    </button>
                  ))}
                  <span className="mx-1 text-slate-300">·</span>
                  {["professional", "friendly", "technical"].map((t) => (
                    <button key={t} type="button" onClick={() => setMetaTone(t)} data-testid={`meta-tone-${t}`}
                      className={`text-xs px-2.5 py-1 rounded-full border ${metaTone === t ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:border-violet-400"}`}>
                      {t}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={generateMeta} disabled={metaBusy || metaInput.trim().length < 3}
                  data-testid="meta-generate"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-semibold disabled:opacity-60">
                  <Sparkles className="w-3.5 h-3.5"/> {metaBusy ? "Generating…" : "Generate"}
                </button>
              </div>
              {metaResult && (
                <div className="mt-4 p-4 rounded-lg bg-violet-50 border border-violet-200 text-sm space-y-3" data-testid="meta-result">
                  <MetaField label="Meta title" value={metaResult.meta_title} onCopy={copyText} testId="meta-field-title"/>
                  <MetaField label="Meta description" value={metaResult.meta_description} onCopy={copyText} testId="meta-field-description"/>
                  <MetaField label="OG title" value={metaResult.og_title} onCopy={copyText} testId="meta-field-og-title"/>
                  <MetaField label="OG description" value={metaResult.og_description} onCopy={copyText} testId="meta-field-og-description"/>
                  <MetaField label="Twitter card" value={metaResult.twitter_card_text} onCopy={copyText} testId="meta-field-twitter"/>
                  <MetaField label="Facebook caption" value={metaResult.facebook_caption} onCopy={copyText} testId="meta-field-fb"/>
                  <MetaField label="LinkedIn caption" value={metaResult.linkedin_caption} onCopy={copyText} testId="meta-field-li"/>
                  <MetaField label="Hashtags" value={(metaResult.hashtags || []).join(" ")} onCopy={copyText} testId="meta-field-hashtags"/>
                  <MetaField label="Slug" value={metaResult.slug_suggestion} onCopy={copyText} testId="meta-field-slug"/>
                  <MetaField label="OG image idea" value={metaResult.og_image_idea} onCopy={copyText} testId="meta-field-og-idea"/>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600 hover:text-slate-900">schema.org JSON-LD</summary>
                    <pre className="mt-2 p-2 bg-white border border-slate-200 rounded text-[10px] overflow-x-auto" data-testid="meta-field-jsonld">{JSON.stringify(metaResult.schema_org_jsonld, null, 2)}</pre>
                  </details>
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-emerald-700"/> Built-in analytics
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Live rollups from the on-page tracker. No Google Analytics needed — everything is stored locally in MongoDB. Use this to spot which referrers convert, which CTAs win, and which personalization variants land.
              </p>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {[1, 7, 30, 90].map((d) => (
                  <button key={d} type="button" onClick={() => setAnalyticsDays(d)} data-testid={`analytics-window-${d}d`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${analyticsDays === d ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"}`}>
                    {d === 1 ? "Last 24h" : `Last ${d}d`}
                  </button>
                ))}
                <button type="button" onClick={loadAnalytics} disabled={analyticsBusy} data-testid="analytics-load"
                  className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold disabled:opacity-60">
                  <Activity className="w-3.5 h-3.5"/> {analyticsBusy ? "Loading…" : "Load summary"}
                </button>
              </div>
              {analyticsResult && (
                <div className="grid md:grid-cols-3 gap-3 text-sm" data-testid="analytics-result">
                  <StatTile label="Total events" value={analyticsResult.total_events}/>
                  <StatTile label="Unique sessions" value={analyticsResult.unique_sessions}/>
                  <StatTile label="Leads captured" value={analyticsResult.leads}/>
                  <BreakdownList title="Top referrers" rows={analyticsResult.by_referrer}/>
                  <BreakdownList title="Visitor types" rows={analyticsResult.by_visitor}/>
                  <BreakdownList title="Devices" rows={analyticsResult.by_device}/>
                  <BreakdownList title="Top pages" rows={analyticsResult.top_pages}/>
                  <BreakdownList title="CTA clicks" rows={analyticsResult.top_ctas}/>
                  <BreakdownList title="By province" rows={analyticsResult.by_province}/>
                  {Array.isArray(analyticsResult.conversion_by_referrer) && analyticsResult.conversion_by_referrer.length > 0 && (
                    <div className="md:col-span-3 p-3 rounded-lg bg-white border border-slate-200" data-testid="analytics-conversion">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Conversion by referrer</div>
                      <div className="space-y-1">
                        {analyticsResult.conversion_by_referrer.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-slate-700">{r.referrer || "direct"}</span>
                            <span className="text-slate-500">{r.leads}/{r.page_views} views</span>
                            <span className={`font-bold ${(r.conversion_pct || 0) > 1 ? "text-emerald-700" : "text-slate-700"}`}>{r.conversion_pct != null ? `${r.conversion_pct}%` : "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3 flex items-center gap-2">
                <Linkedin className="w-3.5 h-3.5 text-sky-700"/> Compose LinkedIn post
              </div>
              <textarea
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                placeholder="Write an ad-hoc update — product news, milestones, or a market take. Posts to BOTH your personal profile and the company page."
                rows={4}
                data-testid="compose-linkedin-text"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 resize-y"
                maxLength={3000}
              />
              <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {["both", "member", "org"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setComposeTarget(t)}
                      data-testid={`compose-target-${t}`}
                      className={`text-xs px-2.5 py-1 rounded-full border ${composeTarget === t ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-300 hover:border-sky-400"}`}
                    >
                      {t === "both" ? "Personal + Company" : t === "member" ? "Personal only" : "Company only"}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[11px] tabular-nums ${composeText.length > 2700 ? "text-amber-700" : "text-slate-500"}`}>{composeText.length}/3000</span>
                  <button
                    type="button"
                    onClick={composeLinkedInPost}
                    disabled={composeBusy || !composeText.trim()}
                    data-testid="compose-linkedin-submit"
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold disabled:opacity-60"
                  >
                    <Linkedin className="w-3.5 h-3.5"/> {composeBusy ? "Posting…" : "Post to LinkedIn"}
                  </button>
                </div>
              </div>
              {composeResult && (
                <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs space-y-1" data-testid="compose-linkedin-result">
                  {Object.entries(composeResult.targets || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2">
                      <span className="font-mono text-slate-700">{k === "member" ? "Personal profile" : "Company page"}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{v.ok ? "posted" : "failed"}</span>
                      <span className="text-slate-500 truncate flex-1 text-right">{v.ok ? (v.external_id || "ok") : (v.error || "")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3 flex items-center gap-2">
                <Linkedin className="w-3.5 h-3.5 text-sky-700"/> LinkedIn auto-post loop
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-bold">every {autopostState?.interval_hours || 5}h</span>
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Generates an original LinkedIn post every <b>{autopostState?.interval_hours || 5} hours</b> and posts to <b>both</b> your personal profile <i>and</i> the company page. Rotates through <b>{autopostState?.rotation_total || 65} (province × audience)</b> cells — 13 provinces × 5 audiences (landlords, property managers, agents, investors, brokers) — for maximum SEO coverage. Brand-voice-aware.
              </p>
              {!autopostState ? (
                <button type="button" onClick={() => loadAutopost()} data-testid="autopost-load"
                  className="text-xs px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-800 text-white font-semibold">Load auto-post status</button>
              ) : (
                <div className="space-y-3" data-testid="autopost-panel">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 rounded bg-white border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Status</div>
                      <div className={`mt-0.5 font-bold ${autopostState.enabled ? "text-emerald-700" : "text-slate-500"}`}>{autopostState.enabled ? "Running" : "Paused"}</div>
                    </div>
                    <div className="p-2 rounded bg-white border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Next post</div>
                      <div className="mt-0.5 font-mono text-[11px]">{autopostState.next_province_name} · {autopostState.next_audience_label}</div>
                    </div>
                    <div className="p-2 rounded bg-white border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Connected</div>
                      <div className="mt-0.5 font-mono text-[11px]">{(autopostState.connected_targets || []).join(", ") || "(none)"}</div>
                    </div>
                    <div className="p-2 rounded bg-white border border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Rotation</div>
                      <div className="mt-0.5 font-mono text-[11px]">{autopostState.rotation_index}/{autopostState.rotation_total}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {autopostState.enabled ? (
                      <button onClick={() => toggleAutopost(false)} disabled={autopostBusy} data-testid="autopost-pause"
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold">Pause loop</button>
                    ) : (
                      <button onClick={() => toggleAutopost(true)} disabled={autopostBusy} data-testid="autopost-resume"
                        className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">Resume loop</button>
                    )}
                    <button onClick={runAutopostNow} disabled={autopostBusy} data-testid="autopost-run-now"
                      className="text-xs px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-800 text-white font-semibold inline-flex items-center gap-1">
                      <Linkedin className="w-3 h-3"/> {autopostBusy ? "Posting…" : "Post one now"}
                    </button>
                    <span className="text-[11px] text-slate-500">Last run: {autopostState.last_run ? new Date(autopostState.last_run).toLocaleString() : "never"}{autopostState.last_status ? ` · ${autopostState.last_status}` : ""}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Email marketing & digital ads</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <button type="button" onClick={runMarketingNow} disabled={marketingBusy} data-testid="admin-marketing-send"
                  className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none"><Mail className="w-5 h-5"/></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{marketingBusy ? "Sending…" : "Send marketing batch now"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Fires the 3-day rotation early: every active subscriber overdue for an email gets the next one in their sequence.</div>
                  </div>
                </button>

                <button type="button" onClick={refreshMarketingStats} data-testid="admin-marketing-stats"
                  className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-sky-500 hover:bg-sky-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-none"><Activity className="w-5 h-5"/></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">Subscriber stats</div>
                    <div className="text-xs text-slate-500 mt-0.5">Active / unsubscribed counts and breakdown by cohort.</div>
                  </div>
                </button>

                <button type="button" onClick={generateAds} disabled={adsBusy} data-testid="admin-ads-generate"
                  className="text-left flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-violet-500 hover:bg-violet-50 transition-colors disabled:opacity-60 sm:col-span-2">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center flex-none"><Megaphone className="w-5 h-5"/></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{adsBusy ? "Generating…" : "Generate Google + Meta ad copy"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Claude writes 5 Google Search + 5 Meta ad variants for the latest blog post. Copy/paste into Ads Manager — or wire up the API stubs for auto-create.</div>
                  </div>
                </button>
              </div>

              {marketingStats && (
                <div className="mt-4 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="admin-marketing-stats-result">
                  <div className="flex flex-wrap gap-2">
                    <Pill tone="green">{marketingStats.active || 0} active</Pill>
                    <Pill tone="slate">{marketingStats.unsubscribed || 0} unsubscribed</Pill>
                    {Object.entries(marketingStats.by_cohort || {}).map(([k, v]) => (
                      <Pill key={k} tone="slate">{k}: {v}</Pill>
                    ))}
                  </div>
                </div>
              )}

              {adCreative && (
                <div className="mt-4 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm space-y-3" data-testid="admin-ads-result">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Ad copy for: {adCreative.blog_title}</div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-1.5">Google Search RSA — {(adCreative.google || []).length} variants</div>
                    {(adCreative.google || []).slice(0, 2).map((g, i) => (
                      <div key={i} className="text-xs mb-2 pb-2 border-b border-slate-200 last:border-0">
                        <div className="font-semibold text-slate-900">{g.headline1} · {g.headline2} · {g.headline3}</div>
                        <div className="text-slate-600 mt-0.5">{g.description1}</div>
                        <div className="text-slate-600">{g.description2}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-blue-700 mb-1.5">Meta (Facebook + Instagram) — {(adCreative.meta || []).length} variants</div>
                    {(adCreative.meta || []).slice(0, 2).map((m, i) => (
                      <div key={i} className="text-xs mb-2 pb-2 border-b border-slate-200 last:border-0">
                        <div className="text-slate-600 italic">{m.primary_text}</div>
                        <div className="font-semibold text-slate-900 mt-0.5">{m.headline}</div>
                        <div className="text-slate-500">{m.description}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 pt-1">Landing URL: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{adCreative.landing_url}</code></div>
                </div>
              )}
            </div>
          </Card>
        )}

        <Card className="p-6 md:col-span-2 border-red-200">
          <h2 className="font-display text-lg font-bold text-red-700 mb-1">Danger zone</h2>
          <p className="text-sm text-slate-600 mb-4" data-testid="settings-deletion-notice">Permanently remove your login and personal profile. Shared rental records and billing/audit history are retained.</p>
          <Button variant="danger" onClick={() => setDeleteOpen(true)} data-testid="delete-account-button">
            Delete account
          </Button>
        </Card>
      </div>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} user={user} onDeleted={accountDeleted}/>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid min-w-0 grid-cols-1 sm:grid-cols-[6rem_minmax(0,1fr)] gap-1 sm:gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`settings-field-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="text-slate-500">{label}</div>
      <div className="min-w-0 text-slate-900 font-medium sm:text-right [overflow-wrap:anywhere]">{value || "—"}</div>
    </div>
  );
}

const PREF_LABELS = {
  product_updates: { title: "Product updates", desc: "New features, bug fixes, security notices." },
  marketing_tips: { title: "Canadian landlord tips", desc: "Every 4 days — plain-English rental / tax guides." },
  referral_reminders: { title: "Referral reminders", desc: "Weekly nudge to share your $50 CAD referral code." },
  trial_engagement: { title: "Trial engagement", desc: "Weekly setup + ROI nudges during your first 14 days." },
  weekly_promo: { title: "Weekly promo digest", desc: "Roundup of new blog posts + product launches." },
};

function EmailPreferencesCard() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/email-preferences");
      setPrefs(data.preferences || {});
    } catch { /* noop */ }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (updated) => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/email-preferences", updated);
      setPrefs(data.preferences);
      toast.success(data.all_off ? "All emails turned off." : "Email preferences saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save preferences");
    } finally { setSaving(false); }
  };

  const toggle = (k) => save({ ...prefs, [k]: !(prefs || {})[k] });
  const turnOffAll = () => save({ unsubscribe_all: true });

  return (
    <Card className="p-6 md:col-span-2" data-testid="email-preferences-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none"><Mail className="w-5 h-5"/></div>
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Email preferences</h2>
            <p className="text-sm text-slate-600 mt-1">Pick exactly which emails you want. Uncheck to stop them immediately.</p>
          </div>
        </div>
        <Button variant="secondary" onClick={turnOffAll} disabled={saving || busy} data-testid="email-prefs-turn-off-all">
          Turn off all emails
        </Button>
      </div>

      <div className="space-y-3 mt-4">
        {Object.entries(PREF_LABELS).map(([k, meta]) => {
          const on = !!(prefs || {})[k];
          return (
            <label key={k} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-400 cursor-pointer" data-testid={`email-pref-row-${k}`}>
              <input
                type="checkbox"
                checked={on}
                disabled={saving || busy}
                onChange={() => toggle(k)}
                className="mt-1 w-4 h-4 accent-emerald-600"
                data-testid={`email-pref-toggle-${k}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">{meta.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{meta.desc}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold ${on ? "text-emerald-700" : "text-slate-400"}`}>{on ? "On" : "Off"}</span>
            </label>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 mt-3">Transactional emails (rent receipts, password resets, tenant messages) are always delivered and cannot be turned off.</p>
    </Card>
  );
}

function MetaField({ label, value, onCopy, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 pb-3 border-b border-violet-200 last:border-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">{label}</div>
        <div className="mt-0.5 text-sm text-slate-800 break-words whitespace-pre-wrap" data-testid={testId}>{value || "—"}</div>
      </div>
      <button type="button" onClick={() => onCopy(value, label)}
        className="flex-none text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 hover:text-violet-900 px-2 py-1 rounded-full border border-violet-200 hover:bg-violet-100"
        data-testid={`${testId}-copy`}>
        Copy
      </button>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-slate-200">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-display font-bold text-slate-900 tabular-nums">{value ?? 0}</div>
    </div>
  );
}

function BreakdownList({ title, rows }) {
  return (
    <div className="p-3 rounded-lg bg-white border border-slate-200">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{title}</div>
      {Array.isArray(rows) && rows.length > 0 ? (
        <div className="space-y-1">
          {rows.slice(0, 6).map((r, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="font-mono text-slate-700 truncate">{r.key || "—"}</span>
              <span className="text-slate-900 font-bold tabular-nums">{r.count}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-400">No data yet</div>
      )}
    </div>
  );
}
